import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { take, catchError } from 'rxjs/operators'
import { Router } from '@angular/router'
import { of, ReplaySubject } from 'rxjs'
import { CollectionEntityType } from 'src/io/models/collection'
import { CollectionFavoriteService } from './collection-favorite.service'
import { CollectionCtaTypes } from './collection-cta.interfaces'

/**
 * Route we redirect to if Sign Up is clicked
 */
export const DEFAULT_REDIRECT_ROUTE = '/signup'
/**
 * Route we redirect to if Favorite is clicked
 */
export const FAVORITE_REDIRECT_ROUTE = '/create-account'

@Component({
  selector: 'flo-collection-cta',
  templateUrl: './collection-cta.component.html',
  styleUrls: ['./collection-cta.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionCtaComponent {
  @Input() public set cta(cta: CollectionCtaTypes | undefined) {
    if (cta) {
      this.ctaSubject.next(cta)
    }
  }
  @Input() public coreNodeId: number
  @Input() public collectionType: CollectionEntityType
  @Input() public classes: string[]
  @Input() public isUserLoggedIn: boolean

  private readonly ctaSubject = new ReplaySubject<string | undefined>(1)

  constructor(private readonly router: Router, private readonly collectionFavoriteService: CollectionFavoriteService) {}

  public cta$ = this.ctaSubject.asObservable()

  public joinNow = (): void => {
    this.router.navigate([DEFAULT_REDIRECT_ROUTE], {
      queryParamsHandling: 'merge',
      queryParams: {
        redirect: this.router.url
      }
    })
  }

  public resumeSubscription = (): void => {
    this.router.navigate(['/account/subscriptions'])
  }

  public favorite = (): void => {
    // We immediately change the button CTA when it's clicked to acknowledge the user's action
    this.ctaSubject.next(CollectionCtaTypes.FAVORITED)
    if (!!this.isUserLoggedIn) {
      this.collectionFavoriteService
        .favoriteByCollectionIdAndType(this.coreNodeId, this.collectionType)
        .pipe(
          take(1),
          catchError(_ => {
            // We flip the button state back if there is an error favoriting
            this.ctaSubject.next(CollectionCtaTypes.FAVORITE)
            return of(undefined)
          })
        )
        .subscribe()
    } else {
      this.router.navigate([FAVORITE_REDIRECT_ROUTE], {
        queryParamsHandling: 'merge',
        queryParams: {
          redirect: `${this.router.url}${this.router.url.includes('?') ? '&' : '?'}coreNodeId=${this.coreNodeId}`,
          register: '1'
        }
      })
    }
  }

  public unfavorite = (): void => {
    // We immediately change the button CTA when it's clicked to acknowledge the user's action
    this.ctaSubject.next(CollectionCtaTypes.FAVORITE)
    this.collectionFavoriteService
      .unfavorite(this.coreNodeId, this.collectionType)
      .pipe(
        take(1),
        catchError(_ => {
          // We flip the button state back if there is an error unfavoriting
          this.ctaSubject.next(CollectionCtaTypes.FAVORITED)
          return of(undefined)
        })
      )
      .subscribe()
  }
}
